import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Mail, RefreshCw } from 'lucide-react';
import API, { resendVerification } from '../services/api';
import toast from 'react-hot-toast';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying | success | already | error
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setStatus('error'); return; }

    API.get(`/auth/verify-email?token=${token}`)
      .then(({ data }) => {
        if (data.already_verified) setStatus('already');
        else setStatus('success');
      })
      .catch(() => setStatus('error'));
  }, [searchParams]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendLoading(true);
    try {
      await resendVerification(resendEmail);
      setResendSent(true);
      toast.success('Verification email sent!');
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center px-4">
      <div className="max-w-md w-full">

        {/* Verifying */}
        {status === 'verifying' && (
          <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
            <Loader className="w-14 h-14 text-purple-600 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email…</h1>
            <p className="text-gray-500">Please wait a moment.</p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
            <p className="text-gray-600 mb-8">
              Your email address has been verified successfully. You can now sign in to your account.
            </p>
            <Link
              to="/login"
              className="inline-block bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold hover:bg-purple-800 transition-colors"
            >
              Sign In Now
            </Link>
          </div>
        )}

        {/* Already verified */}
        {status === 'already' && (
          <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-blue-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Verified</h1>
            <p className="text-gray-600 mb-8">
              Your email is already verified. You're all set — go ahead and sign in.
            </p>
            <Link
              to="/login"
              className="inline-block bg-purple-700 text-white px-8 py-3 rounded-xl font-semibold hover:bg-purple-800 transition-colors"
            >
              Go to Sign In
            </Link>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-lg p-10 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Invalid or Expired</h1>
            <p className="text-gray-600 mb-8">
              This verification link is invalid or has already been used. Enter your email below to get a fresh link.
            </p>

            {!resendSent ? (
              <form onSubmit={handleResend} className="text-left">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Email Address</label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <button
                    type="submit"
                    disabled={resendLoading}
                    className="bg-purple-700 text-white px-4 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-colors flex items-center gap-2 disabled:opacity-60"
                  >
                    {resendLoading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Resend
                  </button>
                </div>
              </form>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <Mail className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-800 font-semibold text-sm">Verification email sent!</p>
                <p className="text-green-600 text-xs mt-1">Check your inbox and click the link.</p>
              </div>
            )}

            <div className="mt-6">
              <Link to="/login" className="text-sm text-purple-600 hover:underline">← Back to Sign In</Link>
            </div>
          </div>
        )}

        {/* Branding */}
        <p className="text-center text-gray-400 text-xs mt-6">
          MDS Transportation LLC · <a href="tel:+13179821959" className="hover:text-purple-600">+1 (317) 982-1959</a>
        </p>
      </div>
    </div>
  );
}
